<script setup>
/**
 * 「收款账号」列表页，逐条对齐旧版 web/35.fbff54e87df92096cd7e.js 里的
 * app-bank 组件（类 a、模板工厂 _ + 子模板 C/v、样式 f）。
 *
 * 旧版这个 chunk 里其实有三个页面，路由表是：
 *   { path: '',          component: app-bank }   收款账号（本页）
 *   { path: 'add',       component: app-add  }   添加收款账号 → BankAddPage.vue
 *   { path: 'edit/:id',  component: app-edit }   编辑收款账号 → BankEditPage.vue
 *
 * 旧版类 a：
 *   ionViewDidEnter → getBankList()
 *   getBankList(l): GET /bank/lists
 *     成功且 data.length == 0 → toast「请添加收款账号」
 *     否则 bankList = data
 *   deleteBank(id): alert{header:'确认', message:'确定删除账号吗？', buttons:[取消, 确认]}
 *     确认后 POST /bank/delete {bank_id} → toast「删除成功」+ 重新 getBankList()
 *   doRefresh(l): getBankList(l)
 *
 * 模板：
 *   ion-header > ion-toolbar
 *     ion-buttons[slot=start] > ion-button[routerLink="/tabs/user"] > ion-icon[name=arrow-back]
 *     ion-title "收款账号"
 *   ion-content            ← 注意这一页没有 padding 属性，内容区不带 16px 内边距
 *     *ngIf="bankList" → div.bank-list > ngFor → div.bank-item
 *         span.bank-title      银行名称
 *         span.bank-username   开户名（点击进编辑页）
 *         span.bank-account    银行账号（点击进编辑页）
 *         span.bank-close[tappable] (click)="deleteBank(bank_id)" > ion-icon[name=close]
 *     div.bank-add-button[routerLink="/user/bank/add"] > ion-icon[name=add]
 *     ion-refresher + ion-refresher-content（参数与其它页一致）
 */
import { onMounted, ref } from 'vue'
import { request } from '../services/api'
import PullRefresher from '../components/PullRefresher.vue'
import LegacyAlert from '../components/LegacyAlert.vue'

// 旧版 bankList 初始是 undefined，模板 *ngIf="bankList" 控制整块显隐
const bankList = ref(null)
// 旧版 deleteBank 里的确认 alert
const confirmId = ref(null)

/**
 * 旧版这些提示走的是 appService.showToast()，也就是 Ionic 的 ion-toast：
 * 屏幕底部浮一条深色圆角条，几秒后自动消失，**不会**在页面里占位置。
 * 之前这里错做成了页面中间的常驻文字，所以列表为空时会一直挂着「请添加收款账号」。
 * 样式按 md toast 的形态还原（深色底、白字、圆角 4px、贴底 16px），
 * 具体数值没有从旧版产物逐条核对（toast 组件在另外的 chunk 里）。
 */
const notice = ref('')
let noticeTimer = null
function showToast(message) {
  notice.value = message
  if (noticeTimer) window.clearTimeout(noticeTimer)
  noticeTimer = window.setTimeout(() => { notice.value = '' }, 2000)
}

/** 旧版 getBankList(l=null) */
async function getBankList(event = null) {
  try {
    const response = await request('/bank/lists')
    const data = response.data || []
    if (data.length === 0) {
      // 旧版这种情况只弹 toast，不给 bankList 赋值（所以列表块不渲染）
      bankList.value = null
      showToast('请添加收款账号')
    } else {
      bankList.value = data
    }
  } catch (error) {
    showToast(error.message || '加载失败')
  } finally {
    event?.complete()
  }
}

/** 旧版 doRefresh(l) */
function doRefresh(event) { void getBankList(event) }

/** 旧版 deleteBank(id) 的 alert 确认 */
function askDelete(bankId) { confirmId.value = bankId }

async function doDelete() {
  const bankId = confirmId.value
  confirmId.value = null
  if (bankId === null) return
  try {
    // 后端 Bank::delete() 读的是 Request::param('bank_id')
    await request('/bank/delete', { method: 'POST', data: { bank_id: bankId } })
    showToast('删除成功')
    await getBankList()
  } catch (error) {
    showToast(error.message || '删除失败')
  }
}

// 旧版是 ionViewDidEnter（每次进入页面都刷新），Vue 里对应 onMounted
onMounted(() => { void getBankList() })
</script>

<template>
  <div class="bank-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮 routerLink="/tabs/user" -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/profile" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">收款账号</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <PullRefresher
      pulling-text="下拉刷新"
      refreshing-text="下拉刷新..."
      refreshing-spinner="circles"
      @refresh="doRefresh"
    >
      <!-- 旧版 C：*ngIf="bankList" -->
      <div v-if="bankList" class="bank-list">
        <div v-for="bank in bankList" :key="bank.bank_id" class="bank-item">
          <span class="bank-title">{{ bank.bank_title }}</span>
          <!-- 旧版开户名和账号都挂了 routerLink 到编辑页 -->
          <RouterLink class="bank-username" :to="`/user/bank/edit/${bank.bank_id}`">{{ bank.bank_username }}</RouterLink>
          <RouterLink class="bank-account" :to="`/user/bank/edit/${bank.bank_id}`">{{ bank.bank_account }}</RouterLink>
          <span class="bank-close" tappable @click="askDelete(bank.bank_id)">
            <!-- 旧版 ion-icon[name=close]，取自 web/svg/md-close.svg -->
            <svg class="ion-icon" viewBox="0 0 512 512" aria-hidden="true"><path d="M405 136.798L375.202 107 256 226.202 136.798 107 107 136.798 226.202 256 107 375.202 136.798 405 256 285.798 375.202 405 405 375.202 285.798 256z" fill="currentColor" /></svg>
          </span>
        </div>
      </div>

      <!-- 旧版 div.bank-add-button[routerLink="/user/bank/add"] -->
      <RouterLink class="bank-add-button" to="/user/bank/add">
        <!-- 旧版 ion-icon[name=add]，取自 web/svg/md-add.svg -->
        <svg class="ion-icon" viewBox="0 0 512 512" aria-hidden="true"><path d="M416 277.333H277.333V416h-42.666V277.333H96v-42.666h138.667V96h42.666v138.667H416v42.666z" fill="currentColor" /></svg>
      </RouterLink>

    </PullRefresher>

    <!-- 旧版 appService.showToast()：底部浮层，不占内容区位置 -->
    <div v-if="notice" class="ion-toast">
      <div class="toast-wrapper"><div class="toast-message">{{ notice }}</div></div>
    </div>

    <!-- 旧版 deleteBank 的确认 alert -->
    <Teleport to="body">
      <LegacyAlert
        v-if="confirmId !== null"
        header="确认"
        message="确定删除账号吗？"
        cancel-text="取消"
        ok-text="确认"
        @cancel="confirmId = null"
        @confirm="doDelete"
      />
    </Teleport>
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
.bank-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#f4f5f8;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}

/* --- 以下逐条照抄旧版样式表 f（chunk 35），:host → .bank-page --- */
/* 注意这一页的 ion-content 没有 padding 属性，所以内容区没有 16px 内边距。
   内容区底色取的是旧版实际渲染结果（浅灰，加号框是白底）：旧版样式表 f 里既没有
   :host{background} 也没给 .bank-add-button 设 background，这个浅灰底应该来自
   Ionic 的 .outer-content 或 main.js 主题 CSS 里我读不到的那一段，
   所以这里以旧版截图的实际观感为准。 */
/* PullRefresher 内部的滚动容器默认是白底（用来遮住 z-index:-1 的下拉刷新层），
   这里穿透改成同样的浅灰，否则会把上面那层底色盖掉。 */
:deep(.refresher-scroll){background:#f4f5f8}

.bank-list{padding:.5rem}
.bank-item{height:80px;border-radius:10px;margin-bottom:20px;padding:10px;position:relative;background:#62b900;color:#fff}
.bank-item .bank-title{font-size:.8rem;display:block;margin-bottom:10px}
.bank-item .bank-username{font-size:1rem;display:block;position:absolute;right:30px;top:10px}
.bank-item .bank-account{font-size:1.5rem;display:inline-block}
.bank-item .bank-close{display:block;position:absolute;bottom:0;right:30px;font-size:2.5rem}
.bank-add-button{margin:.5rem;height:40px;line-height:40px;text-align:center;border:1px solid #ccc;border-radius:10px}
.bank-add-button .ion-icon{font-size:30px;margin-top:4px}

/* 旧版这几处 routerLink 是挂在 div / span 上的，这里用 RouterLink 会渲染成 <a>，
   而 <a> 默认是 inline 且自带链接色，会让上面那些 height / line-height / text-align
   全部失效（加号会缩到左上角并变成蓝色），所以要显式改回块级并继承颜色。 */
.bank-add-button{display:block;color:inherit;text-decoration:none;background:#fff;box-sizing:border-box}
.bank-item .bank-username,.bank-item .bank-account{color:inherit;text-decoration:none}
.bank-item .bank-close{cursor:pointer}

/* ion-icon 的尺寸由 font-size 控制，内部 svg 是 1em */
.ion-icon{width:1em;height:1em;display:inline-block;vertical-align:middle}

/* ion-toast（md）的形态还原。全局样式表里有 ion-toast{z-index:100000}，这里沿用。
   具体尺寸/圆角没有从旧版产物逐条核对。 */
.ion-toast{position:absolute;left:0;right:0;bottom:0;display:flex;justify-content:center;pointer-events:none;z-index:100000}
.toast-wrapper{
  margin:0 16px 16px;max-width:700px;
  border-radius:4px;padding:14px 16px;
  background:#333;color:#fff;font-size:14px;line-height:20px;
  box-shadow:0 3px 5px -1px rgb(0 0 0 / 20%),0 6px 10px 0 rgb(0 0 0 / 14%),0 1px 18px 0 rgb(0 0 0 / 12%);
}
</style>
